<script lang="ts">
  /**
   * レッスン `#/lesson/:lessonId`（lesson-format.md §1、§7、§8）。
   * 描画は LessonView。ここでは進捗の記録（レッスンの段階・write-ac カードの回答）を受け持つ。
   * 記録は画面に出さず、「このレッスンのドリル」も出さない（drillCount を渡さない。problem-first.md 追記）。
   */
  import { onMount, tick } from 'svelte';
  import { SvelteSet } from 'svelte/reactivity';
  import LessonView from '../components/lesson/LessonView.svelte';
  import { setLessonWriteHooks } from '../components/lesson/WriteBlock.svelte';
  import type { LessonDoc } from '../components/lesson/types';
  import { toasts } from '../components/toast-queue.svelte';
  import type { GradeResult } from '../components/types';
  import { isMemoCard, type Card, type MemoCard, type WriteAcCard } from '../lib/cards';
  import { lessonRequiredSteps, resolveLessonSection } from '../lib/lessons';
  import type { AnswerInput, ProgressStore, StoreNotice } from '../lib/progress';
  import { progress as appStore } from '../services';
  import { STAGE_NAMES } from './drill/home-stats';
  import { noticeMessage, type ProgressView } from './drill/progress-view.svelte';
  import { answerResultOf } from './drill/session.svelte';

  interface Props {
    doc: LessonDoc;
    progress: Pick<ProgressView, 'recordAnswer' | 'markLessonStep'>;
    /** カード（formerIds も解決する） */
    findCard: (id: string) => Card | undefined;
    /** ユニットの Stage（見出しの上に出す） */
    stage?: number | undefined;
    /** `#/lesson/<id>#<slug>` の節（problem-format.md §6.3）。見出しへスクロールしてフォーカスする */
    section?: string | undefined;
    /** 問題の画面から来たときの問題 id（「問題に戻る」を出す）。URL には載せない */
    returnTo?: string | null | undefined;
    /** write-ac の書きかけの保存先（progress-store.md の drafts）。既定はアプリの進捗ストア */
    drafts?: Pick<ProgressStore, 'saveDraft' | 'deleteDraft' | 'snapshot'> | undefined;
  }

  let {
    doc,
    progress,
    findCard,
    stage,
    section,
    returnTo = null,
    drafts = appStore,
  }: Props = $props();

  let root: HTMLElement | undefined = $state();

  function reducedMotion(): boolean {
    if (document.documentElement.dataset['motion'] === 'reduce') return true;
    return globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  }

  /**
   * 節の見出しへ移る（§6.3）。hash router なのでブラウザのアンカー移動には頼らない。
   * 該当が無ければレッスンの先頭を出し、何も知らせない（壊れたリンクは verify が防ぐ）。
   */
  function goToSection(target: string | undefined) {
    const hit = target === undefined ? null : resolveLessonSection(doc, target);
    const el = hit?.ok === true ? document.getElementById(`sec-${hit.heading.slug}`) : null;
    if (el) {
      el.scrollIntoView({ block: 'start', behavior: reducedMotion() ? 'auto' : 'smooth' });
      el.focus({ preventScroll: true });
      return;
    }
    if (target === undefined) return;
    window.scrollTo(0, 0);
    root?.querySelector<HTMLElement>('h1')?.focus({ preventScroll: true });
  }

  $effect(() => {
    const target = section;
    void tick().then(() => goToSection(target));
  });

  const requirement = $derived(lessonRequiredSteps(doc));

  function show(notices: readonly StoreNotice[]) {
    for (const n of notices) toasts.show(noticeMessage(n));
  }

  onMount(() => {
    show(progress.markLessonStep(doc.meta.id, { kind: 'open' }, requirement).notices);
  });

  const memos = $derived(
    doc.meta.memos
      .map((id) => findCard(id))
      .filter((c): c is MemoCard => c !== undefined && isMemoCard(c) && c.draft !== true),
  );
  const unitLabel = $derived(
    stage !== undefined ? `Stage ${stage} ${STAGE_NAMES[stage] ?? ''}`.trim() : undefined,
  );

  function findWriteCard(id: string): WriteAcCard | undefined {
    const c = findCard(id);
    return c?.type === 'write-ac' ? c : undefined;
  }

  function onexperiment(id: string) {
    show(progress.markLessonStep(doc.meta.id, { kind: 'experiment', id }, requirement).notices);
  }

  function onwrite(id: string) {
    show(progress.markLessonStep(doc.meta.id, { kind: 'write', id }, requirement).notices);
  }

  /**
   * ヒントを開いた write-ac カードの id。一度開いたら、このレッスンを開いている間は
   * 再採点でも hint: true のまま（CardShell は表示済みのヒントを開き直しても onhint を呼ばない）
   */
  const hinted = new SvelteSet<string>();

  setLessonWriteHooks({
    draftOf: (cardId) => drafts.snapshot().drafts.items[cardId]?.code,
    // スターターに戻したら下書きを消す。AC になっても下書きは残す
    ondraft: (card, code) =>
      show(code === card.starter ? drafts.deleteDraft(card.id) : drafts.saveDraft(card.id, code)),
    onhint: (card) => {
      hinted.add(card.id);
    },
  });

  /** write card="…" の回答はドリルと同じ規則で記録する（§7.3、progress-store.md §4.3） */
  function oncardgrade(card: WriteAcCard, result: GradeResult) {
    const outcome = answerResultOf(result);
    // CE は記録しない（ドリルと同じ）。ヒントの印は外さない
    if (outcome === null) return;
    const hint = hinted.has(card.id);
    const input: AnswerInput = { cardId: card.id, result: outcome, hint };
    if (result.verdict !== 'CE') input.verdict = result.verdict;
    show(progress.recordAnswer(input).notices);
  }
</script>

<div class="page page-drill" bind:this={root}>
  {#if returnTo}
    <p class="back">
      <a href="#/problem/{returnTo}" data-testid="lesson-back-to-problem"
        ><span aria-hidden="true">← </span>問題に戻る</a
      >
    </p>
  {/if}
  <LessonView {doc} {unitLabel} {memos} {findWriteCard} {onexperiment} {onwrite} {oncardgrade} />
</div>

<style>
  .back {
    margin: 0 0 var(--space-4);
  }
  .back a {
    display: inline-flex;
    align-items: center;
    min-height: var(--tap-min);
  }
</style>
